import ContactLink from "@/components/ContactLink";
import { useState } from "react";
import { Head, Link, router, usePage } from "@inertiajs/react";
import { ArrowRight, Sparkles, HeartHandshake, MapPin, ShieldCheck, Zap, QrCode } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import MarketingLayout from "@/components/marketing/MarketingLayout";
import MarketingHero from "@/components/marketing/MarketingHero";
import SectionHeading from "@/components/marketing/SectionHeading";
import Highlight from "@/components/home/Highlight";
import Parallax from "@/components/home/Parallax";
import HeroVideo from "@/components/home/HeroVideo";
import {
  StepImportIllustration,
  StepConnectPaymentsIllustration,
  StepTakeHomeIllustration,
  StepLogMinutesIllustration,
  SectionDivider,
  OpenBookDoodle,
  ClosedBookDoodle,
  SmallStackDoodle,
  FlyingPagesDoodle,
  PencilDoodle,
} from "@/components/home/illustrations";

export default function Home() {
  const { routes } = usePage().props;

  return (
    <MarketingLayout>
      <Head title="A super-easy read-a-thon platform for schools" />

      {/* On mobile we want the activation banner above the hero so parents
          with a take-home sheet land on the action immediately; on desktop
          the hero stays first. flex + order handles the swap. */}
      <div className="flex flex-col">

      {/* ── Hero ────────────────────────────────────────────── */}
      <MarketingHero className="order-2 md:order-1 pt-10 pb-12 md:pt-14 md:pb-16">
        {/* Parallax book scatter — all absolute, pointer-events-none */}
        <Parallax speed={0.18} className="absolute top-8 left-[1%] pointer-events-none hidden xl:block">
          <ClosedBookDoodle size={68} rotate={-14} color="forest" />
        </Parallax>
        <Parallax speed={0.32} className="absolute top-40 left-[1%] pointer-events-none hidden xl:block">
          <PencilDoodle size={72} rotate={32} />
        </Parallax>
        <Parallax speed={0.12} className="absolute bottom-16 left-[1%] pointer-events-none hidden xl:block">
          <SmallStackDoodle size={80} rotate={6} />
        </Parallax>
        <Parallax speed={0.26} className="absolute top-8 right-[1%] pointer-events-none hidden xl:block">
          <OpenBookDoodle size={82} rotate={-8} />
        </Parallax>
        <Parallax speed={0.08} className="absolute bottom-8 right-[1%] pointer-events-none hidden xl:block">
          <FlyingPagesDoodle size={90} rotate={-6} />
        </Parallax>

        <div className="container relative">
          <div className="grid md:grid-cols-[1.05fr_1fr] gap-10 md:gap-12 items-center">
            {/* ── Left: headline + copy + CTAs ─────────── */}
            <div className="space-y-6 md:space-y-7 relative z-10">
              <div className="flex flex-wrap gap-2">
                <Badge className="uppercase tracking-wider">
                  🇨🇦 Built in Canada
                </Badge>
                <Badge variant="warning">
                  <Zap className="h-3 w-3 mr-1.5" />
                  Early adopter discount
                </Badge>
              </div>

              <h1 className="text-5xl md:text-7xl text-foreground leading-[0.95]">
                Read-a-thons
                <br />
                <Highlight color="amber" wraps className="text-primary">
                  that aren't a hassle.
                </Highlight>
              </h1>

              <div className="flourish">
                <span>schools keep what they raise</span>
              </div>

              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl">
                Run your school’s read-a-thon with a flat campaign fee
                and no PagePledge transaction fees. Donors can choose to
                cover the payment provider’s processing fees, so{" "}
                <Highlight color="forest">
                  <span className="text-foreground font-medium">the full donation can reach your school.</span>
                </Highlight>
              </p>

              <div className="flex flex-wrap gap-3 pt-2">
                <Button size="lg" asChild>
                  <Link href={routes.new_school_application}>
                    Sign up your school
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <a href="#how-it-works">See how it works</a>
                </Button>
              </div>
            </div>

            {/* ── Right: overview video ─────────────── */}
            <div className="relative z-10">
              <Parallax speed={-0.05}>
                <HeroVideo />
              </Parallax>
            </div>
          </div>
        </div>
      </MarketingHero>

      {/* ── Parent activation banner ──────────────────────── */}
      <ActivationBanner
        activatePath={routes.activate}
        className="order-1 md:order-2"
      />

      </div>

      {/* ── Trust strip ───────────────────────────────────── */}
      <section className="border-y border-border/60 bg-card/40 py-6 relative overflow-hidden">
        <Parallax speed={0.15} className="absolute -top-4 right-[8%] pointer-events-none hidden md:block">
          <ClosedBookDoodle size={50} rotate={15} color="amber" />
        </Parallax>
        <div className="container relative">
          <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-sm">
            <TrustPill color="primary" icon={<ShieldCheck className="h-4 w-4" />} label="No percentage fees" />
            <TrustPill color="success" icon={<Sparkles className="h-4 w-4" />} label="Early adopter discount" />
            <TrustPill color="forest" icon={<MapPin className="h-4 w-4" />} label="Built in Canada" />
            <TrustPill color="sky" icon={<HeartHandshake className="h-4 w-4" />} label="Donors can cover fees" />
          </div>
        </div>
      </section>

      {/* ── How it works ────────────────────────────────────── */}
      <section id="how-it-works" className="section-py relative overflow-hidden">
        <Parallax speed={0.22} className="absolute top-20 left-[4%] pointer-events-none hidden md:block">
          <OpenBookDoodle size={84} rotate={10} />
        </Parallax>
        <Parallax speed={0.14} className="absolute top-10 right-[5%] pointer-events-none hidden md:block">
          <PencilDoodle size={80} rotate={-18} />
        </Parallax>
        <Parallax speed={0.3} className="absolute bottom-24 right-[8%] pointer-events-none hidden lg:block">
          <ClosedBookDoodle size={60} rotate={22} color="forest" />
        </Parallax>

        <div className="container max-w-6xl relative">
          <SectionHeading
            className="mb-12 md:mb-16"
            flourish="four simple steps"
            title="How it works"
            subhead="Set up your campaign, connect payments, and invite families to start reading and fundraising."
          />

          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
            <FeatureCard
              step="01"
              tint="primary"
              illustration={<StepImportIllustration />}
              title="Import your classrooms"
              description="Download the CSV template, fill in students and classrooms, upload. Classrooms auto-create. Teachers never touch a spreadsheet."
            />
            <FeatureCard
              step="02"
              tint="sky"
              illustration={<StepConnectPaymentsIllustration />}
              title="Connect Square or Stripe"
              description="Connect your school’s Square or Stripe account to receive donations. Your provider charges processing fees, and donors can choose to cover them."
            />
            <FeatureCard
              step="03"
              tint="amber"
              illustration={<StepTakeHomeIllustration />}
              title="Send home the sheets"
              description="PagePledge generates a printable sheet for every student with a QR code. Parents scan the code and verify their email to get started. There’s no password to remember."
            />
            <FeatureCard
              step="04"
              tint="forest"
              illustration={<StepLogMinutesIllustration />}
              title="Track minutes, raise money"
              description="Teachers log classroom reading in bulk. Parents log at home. Donors pledge flat amounts on a share-friendly page. Everyone sees the leaderboard."
            />
          </div>
        </div>
      </section>

      {/* ── By the numbers / value prop ──────────────────── */}
      <section id="pricing" className="section-py border-y border-border/60 bg-card/40 relative overflow-hidden">
        <Parallax speed={0.18} className="absolute top-12 left-[6%] pointer-events-none hidden md:block">
          <FlyingPagesDoodle size={96} rotate={12} />
        </Parallax>
        <Parallax speed={0.1} className="absolute bottom-12 right-[5%] pointer-events-none hidden md:block">
          <SmallStackDoodle size={100} rotate={-4} />
        </Parallax>
        <Parallax speed={0.26} className="absolute top-24 right-[12%] pointer-events-none hidden lg:block">
          <ClosedBookDoodle size={60} rotate={8} color="clay" />
        </Parallax>

        <div className="container max-w-6xl relative">
          <SectionDivider variant="star" className="mb-10 md:mb-14" />

          <SectionHeading
            className="mb-10"
            spaceY="space-y-2"
            flourish="by the numbers"
            title="A fundraiser that adds up."
          />

          <div className="grid gap-8 md:grid-cols-3 text-center relative z-10">
            <BigStat
              value="0%"
              color="primary"
              label="PagePledge transaction fees"
              sub="PagePledge charges only a flat campaign fee. Your payment provider charges processing fees separately."
            />
            <BigStat
              value="~15 min"
              color="forest"
              label="to onboard a school"
              sub="Import your roster, connect Square or Stripe, and print your take-home sheets."
            />
            <BigStat
              value="100%"
              color="amber"
              label="can reach the school"
              sub="Donors can choose to cover the payment provider’s processing fees at checkout."
            />
          </div>

          <SectionDivider variant="dot" className="mt-10 md:mt-14" />
        </div>
      </section>

      {/* ── Big CTA ──────────────────────────────────────── */}
      <section id="apply" className="section-py relative overflow-hidden">
        <Parallax speed={0.14} className="absolute top-10 left-[8%] pointer-events-none hidden md:block">
          <OpenBookDoodle size={78} rotate={-10} />
        </Parallax>
        <Parallax speed={0.24} className="absolute bottom-14 right-[6%] pointer-events-none hidden md:block">
          <PencilDoodle size={80} rotate={-40} />
        </Parallax>
        <Parallax speed={0.08} className="absolute bottom-8 left-[10%] pointer-events-none hidden lg:block">
          <ClosedBookDoodle size={58} rotate={-4} color="amber" />
        </Parallax>

        <div className="container max-w-3xl text-center space-y-6 relative z-10">
          <Badge variant="accent">
            <HeartHandshake className="h-3 w-3 mr-1.5" />
            Early adopter pricing available
          </Badge>
          <SectionHeading
            spaceY="space-y-6"
            subheadClassName="text-lg leading-relaxed"
            title={
              <>
                Ready to{" "}
                <Highlight color="clay">
                  try it
                </Highlight>{" "}
                at your school?
              </>
            }
            subhead="Get an early adopter discount on your campaign, with founder support and hands-on onboarding. Start with a free trial, and pay your school’s flat fee when you’re ready to run your campaign."
          />
          <div className="flex flex-wrap gap-3 justify-center pt-2">
            <Button size="lg" asChild>
              <Link href={routes.new_school_application}>
                Sign up your school
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <ContactLink>Email the founder</ContactLink>
            </Button>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}

// ─────────────────────────────────────────────────────────────
// Local components
// ─────────────────────────────────────────────────────────────

function ActivationBanner({ activatePath, className = "" }) {
  const [code, setCode] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;
    router.get(activatePath, { code: trimmed });
  };

  return (
    <section className={`bg-accent/10 border-y border-accent/20 py-8 md:py-10 ${className}`.trim()}>
      <div className="container">
        <div className="flex flex-col gap-5 md:flex-row md:items-center md:justify-between md:gap-8 max-w-5xl mx-auto">
          <div className="flex items-start gap-4 md:max-w-md">
            <div className="hidden sm:flex h-11 w-11 shrink-0 rounded-full bg-accent/15 text-accent items-center justify-center">
              <QrCode className="h-5 w-5" />
            </div>
            <div className="space-y-1">
              <p className="font-display text-xl text-foreground leading-snug">
                Have a take-home sheet?
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Scan the QR code or enter the code from your sheet to activate your child's reading page.
              </p>
            </div>
          </div>
          <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full md:w-auto md:min-w-[420px]">
            <Input
              name="code"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="BEAR-MOON-4217"
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              aria-label="Activation code"
              className="font-mono tracking-wider sm:flex-1"
            />
            <Button type="submit" size="lg">
              Activate
              <ArrowRight className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}

function TrustPill({ color, icon, label }) {
  const colors = {
    primary: "bg-primary/10 text-primary border-primary/20",
    success: "bg-success/10 text-success border-success/20",
    forest:  "bg-accent/10 text-accent border-accent/20",
    sky:     "bg-info/10 text-info border-info/20",
  }[color];
  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border font-medium ${colors}`}>
      {icon}
      {label}
    </span>
  );
}

function FeatureCard({ step, tint, illustration, title, description }) {
  const tints = {
    primary: {
      bg: "bg-primary/5",
      border: "border-primary/20",
      num: "text-primary",
      imgWrap: "bg-primary/10",
    },
    amber: {
      bg: "bg-warning/5",
      border: "border-warning/20",
      num: "text-warning-foreground",
      imgWrap: "bg-warning/15",
    },
    sky: {
      bg: "bg-info/5",
      border: "border-info/20",
      num: "text-info",
      imgWrap: "bg-info/10",
    },
    forest: {
      bg: "bg-accent/5",
      border: "border-accent/20",
      num: "text-accent",
      imgWrap: "bg-accent/10",
    },
  }[tint];

  return (
    <Card className={`card-hover-lift overflow-hidden ${tints.bg} ${tints.border}`}>
      <div className={`flex items-center justify-center py-6 ${tints.imgWrap} border-b ${tints.border}`}>
        {illustration}
      </div>
      <CardHeader className="space-y-3">
        <div className="flex items-center justify-between">
          <span className={`font-display text-3xl font-semibold ${tints.num}`}>
            {step}
          </span>
          <span className="h-px flex-1 ml-4 bg-border" />
        </div>
        <CardTitle>{title}</CardTitle>
        <CardDescription className="leading-relaxed">{description}</CardDescription>
      </CardHeader>
    </Card>
  );
}

function BigStat({ value, label, sub, color }) {
  const colors = {
    primary: "text-primary",
    forest:  "text-accent",
    amber:   "text-warning-foreground",
  }[color];
  return (
    <div className="space-y-3">
      <div className={`font-display text-6xl md:text-7xl font-semibold leading-none font-tabular ${colors}`}>
        {value}
      </div>
      <p className="font-medium text-foreground">{label}</p>
      <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">{sub}</p>
    </div>
  );
}
